import React, { memo, useCallback, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
    COMPONENT_REGISTRY,
    COMPONENT_SPECIFIC_ROUTES,
    MAIN_ROUTES
} from 'common/config/routes.config';
import Home from 'modules/home/pages/home/Home';
import { useSelector } from 'react-redux';
import { getUserName } from 'common/redux/user/user.selector';
import { RootState } from 'common/redux/core/root.reducer';
import { ComponentRegistryItemWithSubRoutes } from 'common/models/roles-and-access/roleAccess.model';
import { useRolesAndAccess } from 'common/providers/AccessProvider';

function AuthorizedRoutes() {
    const { featuresHierarchy, featuresActions } = useRolesAndAccess();
    const isRouteAllowed = (action: string) => {
        const accesObj = featuresActions.find((a) => a.featureKey === action);
        if (accesObj) {
            return accesObj.read && accesObj.write;
        }
        return false;
    };

    const componentSpecificRoutes = () => {
        const routes = [];
        for (const key in COMPONENT_SPECIFIC_ROUTES) {
            if (isRouteAllowed(COMPONENT_SPECIFIC_ROUTES[key].featureKey)) {
                const Comp = COMPONENT_SPECIFIC_ROUTES[key].component;
                routes.push(
                    <Route
                        path={COMPONENT_SPECIFIC_ROUTES[key].path}
                        element={<Comp />}
                        key={COMPONENT_SPECIFIC_ROUTES[key].label}
                    />
                );
            }
        }
        return routes;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const createRoutes: any = useCallback((data: ComponentRegistryItemWithSubRoutes[]) => {
        return data
            .map((ele) => {
                const ParentComp = ele?.component;
                if (ele?.subRoutes && ele?.subRoutes.length > 0) {
                    return ele.subRoutes.map((element) => {
                        const Comp = element.component;
                        if (element?.subRoutes && element?.subRoutes.length > 0) {
                            return [
                                <Route path={ele.path} element={<ParentComp />} key={ele.label} />,
                                ...createRoutes(element.subRoutes)
                            ];
                        }
                        return <Route path={element.path} element={<Comp />} key={element.label} />;
                    });
                }
                return <Route path={ele.path} element={<ParentComp />} key={ele.label} />;
            })
            .flat(Infinity);
    }, []);
    const userName = useSelector((state: RootState) => getUserName(state));

    const allowedRoutes = useMemo(
        () => createRoutes(featuresHierarchy, COMPONENT_REGISTRY),
        [createRoutes, featuresHierarchy]
    );
    return (
        <>
            <Routes>
                <Route
                    path={MAIN_ROUTES.HOME}
                    element={<Home userName={userName} hasAccess={true} />}
                />
                {componentSpecificRoutes()}
                <Route path={MAIN_ROUTES.DEFAULT} element={<Navigate to={MAIN_ROUTES.HOME} />} />
                {allowedRoutes}
                <Route path={MAIN_ROUTES.WILD} element={<Navigate to={MAIN_ROUTES.HOME} />} />
            </Routes>
        </>
    );
}

export default memo(AuthorizedRoutes);
